/**
 * Prévia do DANFSe v2.0 (Documento Auxiliar da NFS-e Nacional) — mesmo arranjo do PDF oficial.
 * Número, chave de acesso, QR Code e valores finais só existem após a autorização no Sistema Nacional.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import type { DanfsePreviewData } from './buildNacionalDanfseProps';

const BORDER = '1px solid #222';

const Cell: React.FC<{
  label: string;
  children: React.ReactNode;
  span?: number;
  shaded?: boolean;
}> = ({ label, children, span = 1, shaded }) => (
  <Box
    sx={{
      gridColumn: `span ${span}`,
      px: 0.75,
      py: 0.4,
      bgcolor: shaded ? '#f0f0f0' : 'transparent',
      minHeight: 34
    }}
  >
    <Typography sx={{ fontSize: 9, fontWeight: 700, lineHeight: 1.2 }}>{label}</Typography>
    <Typography sx={{ fontSize: 11, lineHeight: 1.25, wordBreak: 'break-word' }}>
      {children || '-'}
    </Typography>
  </Box>
);

const Section: React.FC<{ title: string; cols?: number; children: React.ReactNode }> = ({
  title,
  cols = 4,
  children
}) => (
  <Box sx={{ borderTop: BORDER }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      <Box sx={{ gridColumn: 'span 1', bgcolor: '#e9e9e9', px: 0.75, py: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{title}</Typography>
      </Box>
      {children}
    </Box>
  </Box>
);

const Pending: React.FC<{ what: string }> = ({ what }) => (
  <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
    {what}
  </Box>
);

const NfseNacionalDanfsePreview: React.FC<{ data: DanfsePreviewData }> = ({ data: d }) => (
  <Box
    sx={{
      border: BORDER,
      bgcolor: '#fff',
      color: '#000',
      fontFamily: 'Arial, Helvetica, sans-serif',
      maxWidth: 820,
      mx: 'auto'
    }}
  >
    {/* Cabeçalho */}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 2fr 1.4fr',
        alignItems: 'center',
        bgcolor: '#efefef',
        px: 1,
        py: 0.75
      }}
    >
      <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1b7f5c', letterSpacing: -1 }}>
        NFS<span style={{ color: '#2a5d9f' }}>e</span>
      </Typography>
      <Box textAlign="center">
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>DANFSe v2.0</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Documento Auxiliar da NFS-e</Typography>
      </Box>
      <Box textAlign="right">
        <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Município: {d.municipio}</Typography>
        <Typography sx={{ fontSize: 9 }}>Tipo de Ambiente: {d.tpAmb}</Typography>
        <Typography sx={{ fontSize: 9 }}>{d.tipoAmbienteLabel}</Typography>
      </Box>
    </Box>

    <Box sx={{ borderTop: BORDER, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
      <Cell label="CHAVE DE ACESSO DA NFS-e" span={3}>
        <Pending what="Gerada pelo Sistema Nacional na autorização" />
      </Cell>
      <Cell label="QR Code">
        <Pending what="Gerado na emissão" />
      </Cell>
      <Cell label="NÚMERO DA NFS-e">
        <Pending what="Atribuído na autorização" />
      </Cell>
      <Cell label="COMPETÊNCIA DA NFS-e">{d.competencia}</Cell>
      <Cell label="DATA E HORA DA EMISSÃO DA NFS-e" span={2}>
        <Pending what="Definida na autorização" />
      </Cell>
      <Cell label="NÚMERO DA DPS">{d.nDps}</Cell>
      <Cell label="SÉRIE DA DPS">{d.serie}</Cell>
      <Cell label="DATA E HORA DA EMISSÃO DA DPS" span={2}>
        {d.dhEmissao}
      </Cell>
      <Cell label="EMITENTE DA NFS-e" shaded>
        Prestador
      </Cell>
      <Cell label="SITUAÇÃO DA NFS-e" shaded>
        Prévia — ainda não emitida
      </Cell>
      <Cell label="FINALIDADE" shaded span={2}>
        -
      </Cell>
    </Box>

    <Section title="PRESTADOR / FORNECEDOR">
      <Cell label="CNPJ / CPF / NIF">{d.prestador.cnpj}</Cell>
      <Cell label="Indicador Municipal (Inscrição)">{d.prestador.im}</Cell>
      <Cell label="Telefone">-</Cell>
      <Cell label="Nome / Nome Empresarial" span={2}>
        {d.prestador.nome}
      </Cell>
      <Cell label="Município / Sigla UF">{d.prestador.municipio}</Cell>
      <Cell label="Código IBGE / CEP">{d.prestador.ibgeCep}</Cell>
      <Cell label="Endereço" span={2}>
        {d.prestador.endereco}
      </Cell>
      <Cell label="E-mail" span={2}>
        {d.prestador.email}
      </Cell>
      <Cell label="Simples Nacional na Data de Competência" span={2}>
        {d.prestador.simples}
      </Cell>
      <Cell label="Regime de Apuração Tributária pelo SN" span={2}>
        {d.prestador.regime}
      </Cell>
    </Section>

    <Section title="TOMADOR / ADQUIRENTE">
      <Cell label="CNPJ / CPF / NIF">{d.tomador.doc}</Cell>
      <Cell label="Indicador Municipal (Inscrição)">-</Cell>
      <Cell label="Telefone">-</Cell>
      <Cell label="Nome / Nome Empresarial" span={2}>
        {d.tomador.nome}
      </Cell>
      <Cell label="Município / Sigla UF">{d.tomador.municipio}</Cell>
      <Cell label="Código IBGE / CEP">{d.tomador.ibgeCep}</Cell>
      <Cell label="Endereço" span={2}>
        {d.tomador.endereco}
      </Cell>
      <Cell label="E-mail" span={2}>
        {d.tomador.email}
      </Cell>
    </Section>

    <Box sx={{ borderTop: BORDER, textAlign: 'center', py: 0.25 }}>
      <Typography sx={{ fontSize: 10 }}>
        DESTINATÁRIO DA OPERAÇÃO NÃO IDENTIFICADO NA NFS-e
      </Typography>
    </Box>
    <Box sx={{ borderTop: BORDER, textAlign: 'center', py: 0.25 }}>
      <Typography sx={{ fontSize: 10 }}>
        INTERMEDIÁRIO DA OPERAÇÃO NÃO IDENTIFICADO NA NFS-e
      </Typography>
    </Box>

    <Section title="SERVIÇO PRESTADO">
      <Cell label="Código de Tributação Nacional/Municipal">{d.servico.cTribNac}</Cell>
      <Cell label="Código da NBS">{d.servico.cNbs}</Cell>
      <Cell label="Local da Prestação / Sigla UF / País">{d.servico.local}</Cell>
      <Box sx={{ gridColumn: 'span 4', px: 0.75, py: 0.4 }}>
        <Typography sx={{ fontSize: 11 }}>{d.servico.titulo || '-'}</Typography>
        <Typography sx={{ fontSize: 9, fontWeight: 700, mt: 0.5 }}>Descrição do Serviço</Typography>
        <Typography sx={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {d.servico.descricao || '-'}
        </Typography>
      </Box>
    </Section>

    <Section title="TRIBUTAÇÃO MUNICIPAL (ISSQN)">
      <Cell label="Tipo de Tributação do ISSQN">{d.trib.tipo}</Cell>
      <Cell label="Município / Sigla UF / País de Incidência do ISSQN" span={2}>
        {d.trib.incidencia}
      </Cell>
      <Cell label="BC ISSQN">{d.trib.bc}</Cell>
      <Cell label="Alíquota Aplicada">{d.trib.aliquota}</Cell>
      <Cell label="Retenção do ISSQN">{d.trib.retencao}</Cell>
      <Cell label="ISSQN Apurado">{d.trib.issqn}</Cell>
    </Section>

    <Section title="TRIBUTAÇÃO FEDERAL (EXCETO CBS)">
      <Cell label="IRRF">-</Cell>
      <Cell label="Contribuição Previdenciária - Retida">-</Cell>
      <Cell label="Contribuições Sociais - Retidas">-</Cell>
      <Cell label="PIS - Débito Apuração Própria">-</Cell>
      <Cell label="COFINS - Débito Apuração Própria" span={2}>
        -
      </Cell>
      <Cell label="Descrição Contrib. Sociais - Retidas">-</Cell>
    </Section>

    <Section title="TRIBUTAÇÃO IBS/CBS">
      <Cell label="CST / cClassTrib">{d.ibsCbs ? d.ibsCbs.cstClass : '- / -'}</Cell>
      <Cell
        label="Indicador de Operação / Código IBGE Incidência / Município Incidência / UF"
        span={3}
      >
        {d.ibsCbs ? `${d.ibsCbs.indOp} / - / - / -` : '- / - / - / -'}
      </Cell>
    </Section>

    <Section title="VALOR TOTAL DA NFS-e">
      <Cell label="VALOR DA OPERAÇÃO / SERVIÇO">{d.totais.valor}</Cell>
      <Cell label="Desconto Incondicionado">-</Cell>
      <Cell label="Desconto Condicionado">-</Cell>
      <Cell label="Total das Retenções (ISSQN / Federais)">{d.retencoesTotal}</Cell>
      <Cell label="VALOR LÍQUIDO DA NFS-e">{d.totais.liquido}</Cell>
      <Cell label="Total do IBS/CBS">R$ 0,00</Cell>
      <Cell label="VALOR LÍQUIDO DA NFS-e + IBS/CBS" shaded>
        R$ 0,00
      </Cell>
    </Section>

    <Box sx={{ borderTop: BORDER, px: 0.75, py: 0.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700 }}>INFORMAÇÕES COMPLEMENTARES</Typography>
      <Typography sx={{ fontSize: 10, color: 'text.secondary', fontStyle: 'italic' }}>
        Prévia gerada pelo VOVER a partir da DPS {d.idDps}. Número, chave de acesso, QR Code,
        horário de emissão e valores calculados (alíquota aplicada, ISSQN apurado, totais
        aproximados) são definidos pelo Sistema Nacional na autorização. O DANFSe oficial é obtido
        no ADN depois da emissão.
      </Typography>
    </Box>
  </Box>
);

export default NfseNacionalDanfsePreview;
